<template>
  <div
    ref="containerEl"
    class="flowbuilder-node-container"
  >
    <BaseNode
      ref="nodeEl"
      :node="node"
      :icon="nodeIcon"
      :metrics="metrics"
      :mini-metrics="true"
      classes="bg-fuchsia-100 dark:bg-fuchsia-950 ring-fuchsia-400/50 dark:ring-fuchsia-500/50"
      @click="emit('click')"
      @delete-node="emit('delete-node')"
    >
      <template #context>
        Update Visitor Data:
      </template>

      <div v-html="description" />
    </BaseNode>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, toRefs, inject, type Ref } from 'vue'
import BaseNode from './BaseNode.vue'
import { capitalize, toSentence } from '@rightmessage/shared-utils'
import { getNodeIcon } from '../../utils/nodeIcons'
import type { NodeData } from '../../types/nodes'

/**
 * DataNode Component
 *
 * Updates visitor data including scores, custom fields, and variables.
 *
 * Supported action types:
 * - score: Increment/decrement a score by a value
 * - field: Set a custom field to a value
 * - variable: Set or remove a variable with optional expiration
 *
 * Props:
 * - node: The data node configuration with actions array
 * - icon: Optional custom icon
 * - CodeIcon: Default icon component for data nodes
 * - customFieldLabel: Function to get custom field display name
 */

interface Props {
  node: NodeData
  icon?: any
  CodeIcon: any
  customFieldLabel?: (fieldId: string) => Promise<string>
  tagLabel?: (tagId: string) => Promise<string>
}

const props = withDefaults(defineProps<Props>(), {
  customFieldLabel: async (fieldId: string) => fieldId,
  tagLabel: async (tagId: string) => tagId
})

const emit = defineEmits(['delete-node', 'click'])

const { node } = toRefs(props)

const descriptionText = ref('')

// Inject integration data version for reactivity
const integrationDataVersion = inject('integrationDataVersion', ref(0))
const getNodeMetrics = inject<(node: any) => any>('getNodeMetrics', () => null)
const segmentGroups = inject<Ref<Array<{ id: string; name: string; segments: Array<{ id: string; name: string }> }>>>('segmentGroups', ref([]))

// Get the appropriate icon for this node
const nodeIcon = computed(() => {
  // If an icon is passed as a prop, use it
  if (props.icon) return props.icon

  // Otherwise use the mapped icon
  return getNodeIcon(props.node)
})

// Get metrics for this node
const metrics = computed(() => {
  return getNodeMetrics(props.node)
})

/**
 * Helper function to singularize time units
 */
const formatDuration = (duration: number, unit: string): string => {
  if (duration === 1) {
    // Remove 's' from the end for singularization
    return unit.endsWith('s') ? unit.slice(0, -1) : unit
  }
  return unit
}

/**
 * Extract relevant action properties for description generation
 */
const actionsToDescribe = computed(() => {
  return node.value.actions.map((action) => ({
    type: action.type,
    score: action.score,
    adjust: action.adjust,
    field: action.field,
    value: action.value,
    variableName: action.variableName,
    variableValue: action.variableValue,
    expiresType: action.expiresType,
    expiresDuration: action.expiresDuration,
    expiresUnit: action.expiresUnit,
    tagId: action.tagId,
    segmentGroupId: action.segmentGroupId,
    segmentId: action.segmentId
  }))
})

/**
 * Generate a human-readable description for a single action
 */
const generateDescriptionForAction = async (action: any): Promise<string | null> => {
  if (action.type === 'score') {
    if (!action.score || action.adjust === undefined) {
      return null
    }
    const dirLabel = action.adjust > 0 ? 'increment' : 'decrement'
    const direction = action.adjust > 0 ? '+' : '-'
    return `${dirLabel} score <strong>${action.score}</strong> by ${direction}${Math.abs(action.adjust)}`
  }

  if (action.type === 'field') {
    if (!action.field || !action.value) {
      return null
    }
    const fieldLabel = await props.customFieldLabel(action.field)
    return `Set <strong>${fieldLabel}</strong> to <strong>${action.value}</strong>`
  }

  if (action.type === 'variable') {
    const variableName = action.variableName || ''
    const variableValue = action.variableValue || ''

    if (!variableName && !variableValue) {
      return null
    }

    if (!variableValue.trim() && variableName) {
      return `Remove variable <strong>${variableName}</strong>`
    }

    if (!variableName && variableValue) {
      return null
    }

    let description = `Set variable <strong>${variableName}</strong> to <strong>${variableValue}</strong>`

    if (
      action.expiresType === 'duration' &&
      action.expiresDuration &&
      action.expiresUnit
    ) {
      const formattedUnit = formatDuration(
        action.expiresDuration,
        action.expiresUnit
      )
      description += ` (expires in ${action.expiresDuration} ${formattedUnit})`
    }

    return description
  }

  if (action.type === 'add_tag') {
    if (!action.tagId) {
      return null
    }
    const label = await props.tagLabel(action.tagId)
    return `Add tag <strong>${label}</strong>`
  }

  if (action.type === 'remove_tag') {
    if (!action.tagId) {
      return null
    }
    const label = await props.tagLabel(action.tagId)
    return `Remove tag <strong>${label}</strong>`
  }

  if (action.type === 'set_segment') {
    if (!action.segmentGroupId || !action.segmentId) {
      return null
    }
    const sg = segmentGroups.value.find((g: any) => g.id === action.segmentGroupId)
    const segName = sg?.segments?.find((s: any) => s.id === action.segmentId)?.name || action.segmentId
    const sgName = sg?.name || action.segmentGroupId
    return `Set <strong>${sgName}</strong> to <strong>${segName}</strong>`
  }

  return null
}

/**
 * Regenerate all action descriptions
 */
const generateDescriptions = async () => {
  const descriptions = await Promise.all(
    actionsToDescribe.value.map(generateDescriptionForAction)
  )

  const validDescriptions = descriptions.filter(Boolean)

  if (validDescriptions.length === 0) {
    descriptionText.value = 'Configure actions to update visitor data'
  } else {
    descriptionText.value = capitalize(toSentence(validDescriptions))
  }
}

// Regenerate descriptions when actions change or integration data updates
watch(actionsToDescribe, generateDescriptions, { immediate: true })
watch(integrationDataVersion, generateDescriptions)

const description = computed(() => descriptionText.value)
</script>

